import React from "react";
import { motion } from "framer-motion";
import { ShoppingBag, Share2 } from "lucide-react";
import { LazyImage } from "./LazyImage";
import { ShareButton } from "./ShareButton";
import { HOME } from "@/constants/testIds";

const formatINR = (n) =>
  `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export const ProductCard = ({ product, onBuy, onOpen }) => {
  return (
    <motion.article
      whileHover={{ y: -6 }}
      transition={{ type: "spring", stiffness: 200, damping: 18 }}
      data-testid={HOME.productCard(product.id)}
      className="group relative rounded-2xl overflow-hidden bg-white/70 backdrop-blur-xl backdrop-saturate-150 border border-white/50 shadow-[0_8px_32px_rgba(0,0,0,0.06)] hover:shadow-[0_16px_44px_rgba(0,0,0,0.12)] hover:border-white/80 transition-all duration-300 flex flex-col"
    >
      <button
        type="button"
        onClick={() => onOpen?.(product)}
        data-testid={`product-open-${product.id}`}
        aria-label={`View ${product.title}`}
        className="relative block w-full aspect-[4/5] overflow-hidden focus:outline-none"
      >
        <LazyImage
          src={product.image}
          alt={product.title}
          wrapperClassName="absolute inset-0 w-full h-full"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        />
      </button>
      {/* Floating share — sibling of the open-button to avoid nested <button> markup */}
      <span className="absolute top-3 right-3 z-10" onClick={(e) => e.stopPropagation()}>
        <ShareButton
          product={product}
          testId={`card-share-${product.id}`}
          className="bg-white/85 hover:bg-white text-[#0B0C10] w-9 h-9 p-0"
        />
      </span>

      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#064E3B] font-medium truncate">
              {product.category}
            </p>
            <h3 className="font-display text-lg sm:text-xl text-[#0B0C10] leading-snug mt-1 line-clamp-2">
              {product.title}
            </h3>
          </div>
          <div className="flex flex-col items-end shrink-0 leading-tight">
            <span className="font-display text-lg font-medium text-[#0B0C10]">{formatINR(product.price)}</span>
            {product.original_price > product.price && (
              <span className="text-xs text-neutral-500 line-through">{formatINR(product.original_price)}</span>
            )}
          </div>
        </div>
        {product.description && (
          <p className="text-sm text-neutral-600 line-clamp-2">
            {product.description}
          </p>
        )}
        <button
          data-testid={HOME.buyNowBtn(product.id)}
          onClick={() => onBuy(product)}
          className="mt-auto inline-flex items-center justify-center gap-2 bg-[#0B0C10] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all"
        >
          <ShoppingBag size={15} strokeWidth={1.6} />
          Buy Now
        </button>
      </div>
    </motion.article>
  );
};
